(ns ui.animation-editor.ui
  "The components of the Replicant story template editor: the save
   button, the stage (the iframe with the CreateJS composition), the tab
   bar of the layers and the settings of a layer (texts, fonts, presets
   and the background color)."
  (:require [storrito.ui.spinner :as spinner]
            [ui.animation-editor.model :as model]
            [ui.animation-editor.stage :as stage]
            [ui.animation-editor.templates :as templates]))

(defn save-button
  []
  [:button.flex.flex-col.justify-center.items-center.text-white.rounded-full.shadow-lg.shrink-0
   {:type "button"
    :aria-label "Save"
    :style {:background-color "red"
            :width "75px"
            :height "75px"}
    :on {:click [[:animation-editor/save]]}}
   [:i.fa.fa-save
    {:style {:font-size "230%"}}]
   "Save"])

(defn overlay
  "Covers the editor while the story template is saved, and after it was
   saved until the gallery opens."
  [message]
  [:div.absolute.inset-0.flex.flex-col.justify-center.items-center
   {:style {:background-color "rgba(244, 243, 246, 0.8)"
            :backdrop-filter "blur(2px)"
            :z-index 1002}}
   [:div.mb-6.text-center.px-4
    {:style {:font-size "200%"}}
    message]
   (spinner/vire-spinner-lg)])

(defn stage-frame
  "The iframe with the composition, at a third of the story size. The
   spinner shows until the composition and the background image are
   loaded (`:animation-editor/stage-loaded`)."
  [editor]
  [:div.flex.justify-center.relative
   {:style {:margin-top "76px"}}
   (when-not (:stage-ready? editor)
     [:div.absolute
      {:style {:opacity 0.5}}
      (spinner/vire-spinner-lg)])
   (when (:stage-failed? editor)
     [:div.absolute.inset-0.flex.items-center.justify-center.text-center.text-white.p-4
      "The story template could not be loaded. Please reload the page."])
   [:iframe
    {:replicant/key ::stage
     :id stage/iframe-id
     :src stage/template-path
     :title "Story template preview"
     :width (str (/ 1080 3))
     :height (str (/ 1920 3))
     :style {:border-style "none"
             :background-color "black"}
     :on {:load [[:animation-editor/stage-loaded]]}}]])

(defn tab-bar
  [tabs active-tab]
  [:div.-mb-px.flex.justify-center.border-b.border-gray-300.overflow-auto
   {:aria-label "Tabs"}
   (for [tab tabs]
     (if (= tab active-tab)
       [:div.select-none.whitespace-nowrap.cursor-pointer.border-secondary-500.text-secondary-600.py-2.px-1.text-center.border-b-2.w-24
        {:replicant/key tab
         :aria-current "page"
         :on {:click [[:animation-editor/set-tab tab]]}}
        tab]
       [:div.select-none.whitespace-nowrap.cursor-pointer.border-transparent.text-gray-500.hover:text-gray-700.hover:border-gray-400.py-2.px-1.text-center.border-b-2.w-24
        {:replicant/key tab
         :on {:click [[:animation-editor/set-tab tab]]}}
        tab]))])

(defn presets
  "The presets (the animations) of the `layer` to choose from."
  [entity layer]
  (let [chosen (model/symbol-name entity layer)]
    [:div.flex.flex-col
     [:label.font-medium "Presets:"]
     [:div.flex.flex-wrap.gap-2
      (for [preset (templates/available layer)]
        (let [symbol-name (:symbol-name preset)]
          [:div.shadow.rounded.border.hover:bg-gray-100.flex-none.cursor-pointer
           {:replicant/key symbol-name
            :class (if (= chosen symbol-name)
                     ["bg-gray-200"]
                     [])
            :role "button"
            :aria-pressed (str (= chosen symbol-name))
            :on {:click [[:animation-editor/set-symbol layer symbol-name]]}}
           [:figure.flex.justify-center.relative.text-center
            {:style {:width "150px"}}
            [:img {:src (:icon preset)
                   :alt (:display-name preset)}]
            [:label.font-medium.absolute
             {:style {:top "150px"}}
             (:display-name preset)]]]))]]))

(defn color-input
  [color actions]
  [:input.h-8.w-12.cursor-pointer.rounded.border.border-gray-300.bg-white.p-0
   {:type "color"
    :value color
    :on {:input actions}}])

(defn text-settings
  "The text, the color, the sliders and the font family of a text layer."
  [entity layer label]
  (let [font (get entity (model/font-attr layer))]
    [:div.flex.flex-wrap.gap-4
     [:div.flex.flex-col
      {:style {:width "230px"}}
      [:label.font-medium label]
      [:textarea.border.rounded.p-2
       {:rows 8
        :value (get entity (model/text-attr layer))
        :style {:resize "none"}
        :on {:input [[:animation-editor/set-text layer :event/target.value]]}}]
      [:div.flex.justify-between.items-center.flex-grow
       [:label.font-medium "Text color:"]
       (color-input (:font/color font)
                    [[:animation-editor/set-font layer :font/color :event/target.value]])]]
     [:div.flex.flex-col.gap-3
      {:style {:width "230px"}}
      (for [{:keys [attr label title min max step]} model/font-sliders]
        (let [value (model/slider-value font attr)]
          [:div.flex.flex-col
           {:replicant/key attr}
           [:label.font-medium
            (str label ": " value)]
           [:input
            {:type "range"
             :value (str value)
             :title title
             :min min
             :max max
             :step step
             :on {:input [[:animation-editor/set-font-slider layer attr :event/target.value]]}}]]))]
     [:div.flex.flex-col
      {:style {:height "264px"}}
      [:label.font-medium "Font family:"]
      [:div.h-full.bg-gray-50.border.rounded.overflow-y-scroll
       (for [family templates/fonts]
         [:div.p-2.hover:bg-gray-100.cursor-pointer
          {:replicant/key family
           :class (if (= family (:font/family font))
                    ["bg-gray-200"]
                    [])
           :style {:font-family family}
           :on {:click [[:animation-editor/set-font layer :font/family family]]}}
          family])]]]))

(defn background-settings
  [entity]
  [:div.flex.flex-col.gap-4.flex-grow
   [:div.flex.items-center
    [:label.font-medium.mr-2 "Background color:"]
    (color-input (:instagram.animation.background/color entity)
                 [[:animation-editor/set-background-color :event/target.value]])]
   (presets entity :background)])

(defn layer-settings
  [entity {:keys [layer label]}]
  (if (= :background layer)
    (background-settings entity)
    [:div.flex.flex-col.gap-4.flex-grow
     (text-settings entity layer label)
     (presets entity layer)]))

(defn panel
  "The whole editor for the `editor` state (see
   `ui.animation-editor.actions`)."
  [editor]
  (let [entity (:entity editor)]
    [:div.flex.flex-col.grow.p-4.relative
     (cond
       (:saving? editor)
       (overlay "Saving your story template...")

       (:saved? editor)
       (overlay "Your story template has been saved. Wait until Storrito has rendered the video for you.")

       :else
       (save-button))
     [:div.sm:flex.sm:flex-wrap
      (stage-frame editor)
      [:div.flex.flex-col.ml-4.mt-4.gap-4.w-full
       {:style {:max-width "1024px"}}
       (tab-bar model/tabs (:tab editor))
       [:div.flex.shadow-lg.border.rounded.bg-white.p-4
        (layer-settings entity
                        (model/layer-of-tab (:tab editor)))]]]]))
